import { Button, Modal, Timeline, Tag, Typography, Tooltip, Skeleton, Empty } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { getFormattedEvent } from '../helpers';
import { ResendEmailModal } from './ResendEmailModal';
import * as documentsActions from '../actions';
import { EmailBodyPreviewModal } from '~/_domains/emailsLogs/components/EmailBodyPreviewModal';

const { Text } = Typography;

const EmailSection = styled.div`
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const EmailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.3s ease;
  margin: 2px 0;

  &:hover {
    background-color: #f5f5f5;
    transform: translateX(4px);
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const StyledTimeline = styled(Timeline)`
  .ant-timeline-item {
    padding-bottom: 8px;
  }

  .ant-timeline-item-tail {
    border-left: 2px solid #f0f0f0;
  }

  .ant-timeline-item-head {
    width: 10px;
    height: 10px;
    border: 2px solid #1890ff;
    background-color: #fff;
  }
`;

const TimeText = styled(Text)`
  min-width: 140px;
  color: #8c8c8c;
  font-size: 13px;
  cursor: default;
`;

const ResendButton = styled(Button)`
  color: #1890ff;
  border-color: #1890ff;
  background: transparent;
  padding: 0 8px;
  height: 24px;
  font-size: 13px;
  border-radius: 4px;
  transition: all 0.3s;

  &:hover {
    color: #40a9ff;
    border-color: #40a9ff;
    background: rgba(24, 144, 255, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    color: #096dd9;
    border-color: #096dd9;
    transform: translateY(0);
  }
`;

const EmailAddress = styled(Text)`
  font-size: 15px;
  font-weight: 500;
  color: #262626;
`;

const SkeletonTimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  margin: 2px 0;
`;

const SkeletonTime = styled.div`
  width: 140px;
  min-width: 140px;
`;

const SkeletonStatus = styled.div`
  width: 100px;
`;

const SkeletonButton = styled.div`
  width: 80px;
`;

const SkeletonTimeline = () => (
  <>
    {[1, 2, 3].map((item) => (
      <Timeline.Item key={item}>
        <SkeletonTimelineItem>
          <SkeletonTime>
            <Skeleton active paragraph={false} title={{ width: '100%' }} />
          </SkeletonTime>
          <SkeletonStatus>
            <Skeleton active paragraph={false} title={{ width: '100%' }} />
          </SkeletonStatus>
          <SkeletonButton>
            <Skeleton active paragraph={false} title={{ width: '100%' }} />
          </SkeletonButton>
        </SkeletonTimelineItem>
      </Timeline.Item>
    ))}
  </>
);

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #fafafa;
  border-radius: 8px;
  margin: 16px 0;
`;

const EmptyStateText = styled(Typography.Text)`
  color: #8c8c8c;
  font-size: 14px;
  margin-top: 8px;
`;

export const EmailLogsModal = ({ isOpen, closeModal, documentId }) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [resendEmailModal, setResendEmailModal] = useState({
    isOpen: false,
    data: null,
  });
  const [emailBodyPreviewModal, setEmailBodyPreviewModal] = useState({
    isOpen: false,
    data: null,
    initialLogId: [],
  });

  const resetStates = () => {
    setIsLoading(false);
    setLogs([]);
    setResendEmailModal({
      isOpen: false,
      data: null,
    });
    setEmailBodyPreviewModal({
      isOpen: false,
      data: null,
      initialLogId: [],
    });
  };

  useEffect(() => {
    if (isOpen && documentId) {
      setIsLoading(true);
      void loadLogs();
    }
  }, [isOpen, documentId]);

  const handleCloseModal = () => {
    resetStates();
    closeModal();
  };

  async function loadLogs() {
    try {
      const {
        payload: { data: logsFromApi },
      } = await loadLogsRequest(documentId);
      const formattedData = logsFromApi.map((item) => ({
        ...item,
        formatted_sent_at: moment(item.sent_at).format('DD/MM/YYYY HH:mm'),
        formatted_event: getFormattedEvent(item.event),
      }));
      setLogs(formattedData);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadLogsRequest(id) {
    return dispatch(documentsActions.loadDocumentLogs(id));
  }

  function closeResendEmailModal() {
    setResendEmailModal({
      isOpen: false,
      data: null,
    });
  }

  function openResendEmailModal(data = null) {
    setResendEmailModal({
      isOpen: true,
      data,
    });
  }

  const openEmailBodyPreviewModal = ({ initialLogId, data }) => {
    setEmailBodyPreviewModal({
      isOpen: true,
      data,
      initialLogId,
    });
  };

  const closeEmailBodyPreviewModal = () => {
    setEmailBodyPreviewModal({
      isOpen: false,
      data: null,
      initialLogId: null,
    });
  };

  const groupedLogs = logs.reduce((acc, log) => {
    const key = log.email;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(log);
    return acc;
  }, {});

  const formatTime = (datetime) => {
    moment.locale('pt-br');
    return moment(datetime).fromNow();
  };

  const formatFullDate = (datetime) => {
    moment.locale('pt-br');
    return moment(datetime).format('DD/MM/YYYY [às] HH:mm:ss');
  };

  return (
    <Modal
      title="Logs"
      visible={isOpen}
      onCancel={handleCloseModal}
      footer={false}
      width={800}
    >
      {isLoading ? (
        <SkeletonTimeline />
      ) : logs.length === 0 ? (
        <EmptyStateContainer>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <EmptyStateText>
                Nenhum log encontrado para este documento
              </EmptyStateText>
            }
          />
        </EmptyStateContainer>
      ) : (
        Object.entries(groupedLogs).map(([email, emailLogs]) => (
          <EmailSection key={email}>
            <EmailHeader>
              <EmailAddress>{email}</EmailAddress>
            </EmailHeader>
            <StyledTimeline>
              {emailLogs.sort((a, b) => moment(b.sent_at).diff(moment(a.sent_at))).map((log) => (
                <Timeline.Item key={log.id}>
                  <TimelineItem>
                    <Tooltip title={formatFullDate(log.sent_at)}>
                      <TimeText>{formatTime(log.sent_at)}</TimeText>
                    </Tooltip>
                    <StatusContainer>
                      {log.formatted_event && (
                        <Tag color={log.formatted_event.color} style={{ margin: 0 }}>
                          {log.formatted_event.text}
                        </Tag>
                      )}
                      {(log.event === 'draft' || log.event === 'failed') && (
                        <ResendButton
                          type="link"
                          icon="mail"
                          onClick={() => openResendEmailModal({ emailTo: email, documentId })}
                        >
                          Reenviar
                        </ResendButton>
                      )}
                      {log.event === 'pendent' && (
                        <Button
                          icon="eye"
                          size="small"
                          type="link"
                          style={{ padding: '0 8px', height: '24px' }}
                          onClick={() => {
                            const deliveryLogs = logs
                              .filter(({ event }) => event === 'delivered')
                              .sort((a, b) => moment(a.sent_at).diff(b.sent_at));

                            if (deliveryLogs.length > 0) {
                              const latestDeliveryLog = deliveryLogs[0];
                              openEmailBodyPreviewModal({
                                initialLogId: log.id,
                                data: {
                                  id: log.id,
                                  ...latestDeliveryLog,
                                },
                              });
                            }
                          }}
                        />
                      )}
                    </StatusContainer>
                  </TimelineItem>
                </Timeline.Item>
              ))}
            </StyledTimeline>
          </EmailSection>
        ))
      )}
      <ResendEmailModal
        isOpen={resendEmailModal.isOpen}
        closeModal={closeResendEmailModal}
        data={resendEmailModal.data}
        loadLogs={loadLogs}
      />
      <EmailBodyPreviewModal
        isOpen={emailBodyPreviewModal.isOpen}
        onClose={closeEmailBodyPreviewModal}
        initialLogId={emailBodyPreviewModal.initialLogId}
        data={emailBodyPreviewModal.data}
      />
    </Modal>
  );
};
